import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { AnalysisSinglePersonResponse } from '@aibees/elephant/types/personality-analysis';
import { Maybe, Nullable, UserRelation } from '@aibees/shared/definition';
import {
  Company,
  CompanyDepartmentKeys,
  CompanyRelevancy,
  CompanyTechnologies,
  GeoLocation,
} from '../search-table-company';

export interface SearchTablePeopleProps {
  className?: string;
}

export enum PeopleEmailStatus {
  LOCKED = 'LOCKED', // not sent by the backend, only usable in the frontend
  UPDATE = 'UPDATE', // not sent by the backend, only usable in the frontend
  OUTDATED = 'OUTDATED', // not sent by the backend, only usable in the frontend
  VALID = 'VALID',
  CATCH_ALL = 'CATCH_ALL',
  INVALID = 'INVALID',
  ERROR = 'ERROR',
  UNKNOWN = 'UNKNOWN',
  DOMAIN_NOT_FOUND = 'DOMAIN_NOT_FOUND',
  DOMAIN_NOT_EXIST = 'DOMAIN_NOT_EXIST',
  DOMAIN_NOT_ACCEPT = 'DOMAIN_NOT_ACCEPT',
  DO_NOT_MAIL = 'DO_NOT_MAIL',
  NOT_FOUND = 'NOT_FOUND',
  EXPIRED = 'EXPIRED',
}

export enum PersonEmailDomainType {
  SMTP = 'SMTP',
  CATCH_ALL = 'CATCH_ALL',
  UNKNOWN = 'UNKNOWN',
}

export enum PersonEmailServiceProvider {
  MV = 'MV',
  ZB = 'ZB',
  IP = 'IP',
}

export type EmailItem = {
  // null when Find Email ran and came back with nothing
  address: Nullable<string>;
  status: PeopleEmailStatus;
  free: boolean;
  found: boolean;
  new: boolean;
  date: string;
  score: number;
  provider: PersonEmailServiceProvider;
  domainType: PersonEmailDomainType;
  mx: Nullable<{
    found: boolean;
    smtpProvider: string;
    google: boolean;
    catchall: boolean;
    mxrecord: string;
  }>;
};

export type PeopleEmail = {
  content?: EmailItem[];
  created: string;
  refId: string;
  state: ENRICHMENT_STATE;
};

export type PeoplePhoneNumberContent = {
  raw: string;
  sanitized: string;
};

export type PeoplePhoneNumber = {
  created: string;
  refId: string;
  content: {
    description: string;
    home: PeoplePhoneNumberContent;
    primary: PeoplePhoneNumberContent;
    work: PeoplePhoneNumberContent;
    other: unknown[];
    status: string;
  };
  state: ENRICHMENT_STATE;
};

export enum PersonContactDetailsItem {
  EXPERIENCE = 'EXPERIENCE',
  EDUCATIONS = 'EDUCATIONS',
  PUBLICATIONS = 'PUBLICATIONS',
  VOLUNTEER_EXPERIENCES = 'VOLUNTEER_EXPERIENCES',
  CERTIFICATIONS = 'CERTIFICATIONS',
  PROJECTS = 'PROJECTS',
  RECOMMENDATION = 'RECOMMENDATION',
  LANGUAGES = 'LANGUAGES',
}

export type PeopleContactDetailsState = {
  created: string;
  refId: string;
  state: ENRICHMENT_STATE;
  content?: PersonContactDetailsItem[];
};

export interface RevelationPhoneCached {
  content: Nullable<PeopleMobilePhoneNumber>;
  created: string;
  owner: Nullable<string>;
  refId: string;
  state: ENRICHMENT_STATE;
}

export type Revelation = {
  analysis: Nullable<AnalysisSinglePersonResponse>;
  email: Nullable<PeopleEmail>;
  contact: Nullable<PeopleContactDetailsState>;
  phone: Nullable<PeoplePhoneNumber>;
  phone_cached: Nullable<RevelationPhoneCached>;
};

export interface PersonPicture {
  source: Nullable<string>;
  uploaded: Nullable<boolean>;
}

export interface PersonProfile {
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  full_name: Nullable<string>;
  headline: Nullable<string>;
  title: Nullable<string>;
  picture: Nullable<PersonPicture>;
  background: Nullable<PersonPicture>;
  birth_date: Nullable<string>;
  summary: Nullable<string>;
}

export interface PersonLink {
  facebook: Nullable<string>;
  github: Nullable<string>;
  linkedin: Nullable<string>;
  twitter: Nullable<string>;
  instagram: Nullable<string>;
}

export interface PersonLocation {
  country: Nullable<string>;
  data: Nullable<string>;
  state: Nullable<string>;
  city: Nullable<string>;
  position: Nullable<GeoLocation>;
  default: Nullable<string>;
  short: Nullable<string>;
}

export interface LanguageLocale {
  country: Nullable<string>;
  language: Nullable<string>;
}

export enum LanguageProficiency {
  PROFESSIONAL_WORKING = 'PROFESSIONAL_WORKING',
  ELEMENTARY = 'ELEMENTARY',
  NATIVE_OR_BILINGUAL = 'NATIVE_OR_BILINGUAL',
}

export interface ProfileLanguageItem {
  name: Nullable<string>;
  proficiency: Nullable<LanguageProficiency>;
}

export interface PersonLanguages {
  primary_locale: Nullable<LanguageLocale>;
  supported_locales: Nullable<LanguageLocale[]>;
  profile_languages: Nullable<ProfileLanguageItem[]>;
}

export interface PersonSchool {
  id: Nullable<string>;
  name: Nullable<string>;
  logo: Nullable<string>;
  url: Nullable<string>;
}

export interface DateRange {
  start: Nullable<string>;
  end?: Nullable<string>;
}

export interface PersonEducation {
  school: Nullable<PersonSchool>;
  degree_name: Nullable<string>;
  field_of_study: Nullable<string>;
  grade: Nullable<string>;
  date: Nullable<DateRange>;
}

export interface PersonAward {
  title: Nullable<string>;
  description: Nullable<string>;
  issuer: Nullable<string>;
  date: Nullable<string>;
}

export interface PersonCompanyGeneralInfo {
  id: Nullable<string>;
  name: Nullable<string>;
  logo: Nullable<string>;
  url: Nullable<string>;
}

export interface PersonCertification {
  name: Nullable<string>;
  authority: Nullable<string>;
  url: Nullable<string>;
  license_number: Nullable<string>;
  display_source: Nullable<string>;
  company: Nullable<PersonCompanyGeneralInfo>;
  date: Nullable<DateRange>;
}

export interface PersonProjectContributor {
  profile_id: Nullable<string>;
  type: Nullable<string>;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  sub_title: Nullable<string>;
  headline: Nullable<string>;
  profile_picture: Nullable<string>;
}

export interface PersonProject {
  title: Nullable<string>;
  description: Nullable<string>;
  date: Nullable<DateRange>;
  contributors: Nullable<PersonProjectContributor[]>;
}

export interface PersonPublicationAuthor {
  profile_id: Nullable<string>;
  type: Nullable<string>;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  sub_title: Nullable<string>;
  headline: Nullable<string>;
  profile_picture: Nullable<string>;
}

export interface PersonPublication {
  name: Nullable<string>;
  publisher: Nullable<string>;
  url: Nullable<string>;
  date: Nullable<string>;
  authors: Nullable<PersonPublicationAuthor[]>;
}

export interface PersonCourse {
  name: Nullable<string>;
  number: Nullable<string>;
}

export interface PersonStatisticsNetwork {
  influencer: Nullable<boolean>;
  followable: Nullable<boolean>;
  followers_count: Nullable<number>;
  connections_count: Nullable<number>;
}

export interface PersonExperienceTenure {
  company: Nullable<string>; // latest company start data
  start: Nullable<string>; // start career date
  title: Nullable<string>; // start latest title date
  role: Nullable<string>; // start latest role date
}

export interface PersonExperience {
  prev_job_titles: Nullable<string[]>;
  tenure: Nullable<PersonExperienceTenure>;
}

export interface PersonStatistics {
  network: Nullable<PersonStatisticsNetwork>;
  experience: Nullable<PersonExperience>;
}

export type PersonCompany = Pick<
  Company,
  | 'id'
  | 'summary'
  | 'link'
  | 'analytics'
  | 'financial'
  | 'retail'
  | 'location'
  | 'technologies'
  | 'industries'
  | 'keywords'
  | 'hashtags'
  | 'languages'
  | 'sub_organizations'
  | 'contact'
>;

export interface PersonDepartment {
  departments: Nullable<CompanyDepartmentKeys[]>;
  subDepartments: Nullable<string[]>;
  functions: Nullable<string[]>;
  seniority: Nullable<string>;
}

export interface PersonProfilePosition {
  date: Nullable<DateRange>;
  employment_type: Nullable<string>;
  description: Nullable<string>;
  company: Nullable<string>;
  location: Nullable<string>;
  title: Nullable<string>;
}

export interface PersonPostionGroup {
  date: Nullable<DateRange>;
  profile_positions: Nullable<PersonProfilePosition[]>;
  company: Nullable<PersonCompanyGeneralInfo>;
  current: Nullable<boolean>;
}

export interface PersonTestScore {
  name: Nullable<string>;
  score: Nullable<string>;
  date: Nullable<string>;
}

export interface PersonMemberBadges {
  openToWork: Nullable<boolean>;
  premium: Nullable<boolean>;
  creator: Nullable<boolean>;
  hiring: Nullable<boolean>;
  verified: Nullable<boolean>;
}

export interface PersonVolunteerExperience {
  role: Nullable<string>;
  company: Nullable<PersonCompanyGeneralInfo>;
  cause: Nullable<string>;
  description: Nullable<string>;
  date: Nullable<DateRange>;
}

export interface PersonTreasuryMedia {
  title: Nullable<string>;
  description: Nullable<string>;
  url: Nullable<string>;
  image: Nullable<string>;
}

export interface PersonPatentInventor {
  profile_id: Nullable<string>;
  type: Nullable<string>;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  sub_title: Nullable<string>;
  headline: Nullable<string>;
  profile_picture: Nullable<string>;
}

export interface PersonPatent {
  title: Nullable<string>;
  issuer: Nullable<string>;
  description: Nullable<string>;
  date: Nullable<string>;
  patent_number: Nullable<string>;
  application_number: Nullable<string>;
  inventors: Nullable<PersonPatentInventor[]>;
}

export interface PersonOrganization {
  name: Nullable<string>;
  position: Nullable<string>;
  date: Nullable<DateRange>;
}

export enum PersonalityAnalysisUpdatableItem {
  HEADLINE = 'HEADLINE',
  SKILLS = 'SKILLS',
  SUMMARY = 'SUMMARY',
}

export enum EmailUpdatableItem {
  EMAIL = 'EMAIL',
}

export enum CompanyUpdatableItem {
  COMPANY = 'COMPANY',
}

export interface PersonUpdatable {
  CONTACT: Nullable<PersonContactDetailsItem[]>;
  EMAIL: Nullable<EmailUpdatableItem[]>;
  ANALYSIS: Nullable<PersonalityAnalysisUpdatableItem[]>;
  // a company (job) change; single-member array, like EMAIL
  COMPANY: Nullable<CompanyUpdatableItem[]>;
}

// backend-supplied update counters, one per drawer section plus their sum
export interface PersonUpdates {
  profileInsights: number;
  contactDetails: number;
  analysis: number;
  total: number;
}

export type PeopleMobilePhoneNumber = Array<string>;

export type PhoneNumber = {
  mobile: Nullable<PeopleMobilePhoneNumber>;
};

export interface PeopleContact {
  phone: Nullable<PhoneNumber>;
}

export interface Person {
  id: string;
  canSimilaritySearch?: boolean;
  profile: Maybe<PersonProfile>;
  contact: Nullable<PeopleContact>;
  link: Nullable<PersonLink>;
  location: Nullable<PersonLocation>;
  languages: Nullable<PersonLanguages>;
  industry: Nullable<string>;
  technologies: Nullable<CompanyTechnologies[]>;
  educations: Nullable<PersonEducation[]>;
  awards: Nullable<PersonAward[]>;
  certifications: Nullable<PersonCertification[]>;
  projects: Nullable<PersonProject[]>;
  publications: Nullable<PersonPublication[]>;
  courses: Nullable<PersonCourse[]>;
  skills: Nullable<string[]>;
  statistics: Nullable<PersonStatistics>;
  testScores: Nullable<PersonTestScore[]>;
  memberBadges: Nullable<PersonMemberBadges>;
  volunteerExperiences: Nullable<PersonVolunteerExperience[]>;
  treasuryMedia: Nullable<PersonTreasuryMedia[]>;
  patents: Nullable<PersonPatent[]>;
  organizations: Nullable<PersonOrganization[]>;
  company: Nullable<PersonCompany>;
  department: Nullable<PersonDepartment>;
  positionGroups: Nullable<PersonPostionGroup[]>;
  available: Nullable<PersonContactDetailsItem[]>;
  saved: boolean;
  user?: UserRelation;
  revelation?: Revelation;
  labels: string[];
  relevancy: Nullable<CompanyRelevancy>;
  changes: Nullable<PersonUpdatable>;
  updates: Nullable<PersonUpdates>;
}

export type PeopleTableInfo = Omit<
  Person,
  | 'organizations'
  | 'patents'
  | 'treasuryMedia'
  | 'volunteerExperiences'
  | 'memberBadges'
  | 'testScores'
  | 'skills'
  | 'languages'
  | 'educations'
  | 'awards'
  | 'certifications'
  | 'projects'
  | 'publications'
  | 'courses'
  | 'statistics'
>;

// 'updates' has to stay in this Pick — without it every socket-driven path
// type-erases the field while still carrying it at runtime (D-16 caveat 5)
export type PersonChangesInfo = Pick<
  Person,
  | 'id'
  | 'changes'
  | 'labels'
  | 'revelation'
  | 'saved'
  | 'user'
  | 'available'
  | 'updates'
>;
